'use client';

import { useTranslations } from 'next-intl';
import type { RefObject } from 'react';

import CrossIcon from '@/fo/icons/base/icons/cross24.svg';
import { UiArticleTileBase } from '@/fo/ui/article/tile/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitLabelBase, UiKitLabelBaseProps } from '@/fo/ui/kit/label/base';
import { UiKitPaginationBase } from '@/fo/ui/kit/pagination/base';
import { UiModHeadFixed } from '@/fo/ui/mod/head/fixed';

import { FilterPanel } from '../components/FilterPanel/FilterPanel';
import { ListPanel } from '../components/ListPanel/ListPanel';
import { ArticleItem, FilterOption, SortOptionEnum } from '../types';

import { styles } from './UiArticleListBase.styles';

export interface UiArticleListPaginationProps {
  pageCount: number;
  page: number;
  onPageChange: (page: number) => void | Promise<void>;
  loading?: boolean;
  getPageHref: (page: number) => string;
}

export interface UiArticleListBaseProps {
  items: ArticleItem[];
  content?: React.ReactNode;
  filters?: FilterOption[];
  selectedFilters?: string[];
  onFilterChange?: (value: string[]) => void;
  onSortChange?: (value: SortOptionEnum) => void;
  sort?: SortOptionEnum;
  onSearch?: (value: string) => void;
  pagination?: UiArticleListPaginationProps;
  onLoadMore?: () => void;
  showMore?: boolean;
  loadingMore?: boolean;
  tag?: UiKitLabelBaseProps;
  requestCancelUrl?: string;
  rootRef?: React.RefObject<HTMLDivElement | null>;
  showSticky?: boolean;
  isVideo?: boolean;
  initialQuery?: string;
  advertisingWarningText?: string;
  listScrollAnchorRef?: RefObject<HTMLDivElement | null>;
}

export function UiArticleListBase({
  items,
  content = null,
  filters,
  selectedFilters,
  onFilterChange,
  onSortChange,
  sort,
  onSearch,
  pagination,
  onLoadMore,
  showMore,
  loadingMore,
  tag,
  requestCancelUrl,
  rootRef,
  showSticky,
  isVideo,
  initialQuery,
  advertisingWarningText,
  listScrollAnchorRef
}: UiArticleListBaseProps) {
  const slots = styles({ loadingMore });

  const t = useTranslations();

  return (
    <div className={slots.base()}>
      {filters && (
        <>
          <FilterPanel
            filters={filters}
            selectedFilters={selectedFilters || []}
            onFilterChange={onFilterChange}
            rootRef={rootRef}
          />
          <UiModHeadFixed
            order={1}
            show={showSticky}
            positioning="fixed"
          >
            <div className={slots.stickyFilterPanelContainer()}>
              <FilterPanel
                filters={filters}
                selectedFilters={selectedFilters || []}
                onFilterChange={onFilterChange}
                className={slots.stickyFilterPanel()}
              />
            </div>
          </UiModHeadFixed>
        </>
      )}
      <ListPanel
        onSortChange={onSortChange}
        sort={sort}
        onSearch={onSearch}
        initialQuery={initialQuery}
      />

      {tag && (
        <div className={slots.requestContainer()}>
          <div className={slots.request()}>
            <div className={slots.requestName()}>
              {t('article.page.blog.articlesOnRequest')}
            </div>

            <UiKitLabelBase
              size="m"
              uppercase
              className={slots.requestLabel()}
              color="yellow"
              customColor={tag.color}
              textColor={tag.color ? 'var(--color-white)' : undefined}
              {...tag}
            >
              {tag.children}
            </UiKitLabelBase>

            {requestCancelUrl && (
              <UiKitButtonBase
                className={slots.requestCancelButton()}
                childrenClassName={slots.requestCancelButtonChildren()}
                as="link"
                href={requestCancelUrl}
                color="ghost"
              >
                <span>{t('reset')}</span>
                <CrossIcon className={slots.requestCancelButtonIcon()} />
              </UiKitButtonBase>
            )}
          </div>
        </div>
      )}

      <div className={slots.container()}>
        {listScrollAnchorRef && (
          <div
            ref={listScrollAnchorRef}
            className="pointer-events-none h-0 w-full shrink-0"
            aria-hidden
          />
        )}
        <div className={slots.list()}>
          {items.map((item) => (
            <UiArticleTileBase
              isVideo={isVideo}
              key={item.id}
              article={item}
              withoutButton
              withMicrodata
              advertisingWarningText={advertisingWarningText}
            />
          ))}
        </div>
        {pagination && pagination.pageCount > 1 && (
          <div className={slots.pagination()}>
            <UiKitPaginationBase
              count={pagination.pageCount}
              page={pagination.page}
              onChange={pagination.onPageChange}
              disabled={pagination.loading}
              getPageHref={pagination.getPageHref}
              disabledNavigation
              disabledMore
            />
          </div>
        )}
        {showMore && !pagination && (
          <div className={slots.showMore()}>
            <button
              type="button"
              className={slots.showMoreButton()}
              disabled={loadingMore}
              onClick={onLoadMore}
            >
              <span className={slots.loading()} />
              <span className={slots.loadMoreButtonText()}>
                {t('showMore')}
              </span>
            </button>
          </div>
        )}
      </div>
      {content}
    </div>
  );
}
